import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';

import {
	isAccountCategory,
	isAccountDateRange,
	isAccountSlug,
	isOrder,
	isPurchasesCompleteStatusTypes,
	isSortBy,
	parseHideDelivered,
} from 'Components/Account/AccountPage.helpers';
import type { AccountDateRange, SortBy } from 'Types/account';
import { AccountCategory, AccountSlugs } from 'Types/account';
import type { Order } from 'Types/generic';
import type { PurchasesCompleteStatusTypes } from 'Types/purchaseCompleteStatus';
import ROUTES from 'Utilities/routes';

export type UseAccountRouterReturnType =
	| {
			category: AccountCategory;
			changePage: (query: ParsedUrlQuery) => void;
			dateRangeType?: AccountDateRange;
			hideDelivered: boolean;
			keyword?: string;
			order?: Order;
			purchasesCompleteStatusType?: PurchasesCompleteStatusTypes;
			resolvePageQueryParams: (newQuery: ParsedUrlQuery) => ParsedUrlQuery;
			slug: AccountSlugs;
			sortBy?: SortBy;
	  }
	| Record<string, never>;

const useAccountRouter = (): UseAccountRouterReturnType => {
	const { push, query } = useRouter();

	const { dateRangeType, keyword, order, params: urlParams, purchasesCompleteStatusType, sortBy } = query || {};

	const [category, slug] = urlParams || [];

	const resolvePageQueryParams = (newQuery: ParsedUrlQuery) => {
		const params = {
			// ...query,
			...newQuery,
		};

		return params;
	};

	const changePage = (newQuery: ParsedUrlQuery) => {
		/**
		 * To do shallow routing on dynamic route, we have to interpolate dynamic route with the query.
		 * @source https://github.com/vercel/next.js/blob/master/errors/href-interpolation-failed.md
		 * @source https://nextjs.org/docs/api-reference/next/router#with-url-object
		 *
		 * We use shallow routing here because we don't want to run `getInitialProps` on browser routing
		 * @source https://nextjs.org/docs/routing/shallow-routing
		 */
		void push(
			{
				pathname: ROUTES.ACCOUNT.href,
				query: {
					...query,
					...newQuery,
					page: 1, // we send user to 1st page when he changes section, so we need to reflect it in the url
				},
			},
			undefined, // this is not needed, we let next to compose the asPath based on url setting.
			{ shallow: true },
		);
	};

	/**
	 * If user tries to access page with invalid slug, category, or any other param, we redirect the user to the default account page.
	 */
	if (
		!isAccountSlug(slug) ||
		!isAccountCategory(category) ||
		(dateRangeType !== undefined && !isAccountDateRange(dateRangeType)) ||
		(order !== undefined && !isOrder(order)) ||
		(sortBy !== undefined && !isSortBy(sortBy)) ||
		(purchasesCompleteStatusType !== undefined && !isPurchasesCompleteStatusTypes(purchasesCompleteStatusType))
	) {
		void push(ROUTES.ACCOUNT.as(AccountCategory.BIDS, AccountSlugs.ACTIVE));

		return {};
	}

	return {
		category,
		changePage,
		dateRangeType,
		hideDelivered: parseHideDelivered(query),
		keyword: keyword ? String(keyword) : undefined,
		order,
		purchasesCompleteStatusType,
		resolvePageQueryParams,
		slug,
		sortBy,
	};
};

export default useAccountRouter;
